import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { CalendarEntryMedia, renameMediaInputSchema } from '../mediaSchemas';
import { EVENT_DIALOG_MAX_WIDTH, EVENT_DIALOG_PAPER_SX } from '../dialogLayout';
import AuthenticatedImage from '../../core/AuthenticatedImage';
import { primaryButtonSx, secondaryButtonSx, dangerButtonSx } from '../../core/pvtaStyles';

interface MediaGalleryDialogProps {
    open: boolean;
    media: CalendarEntryMedia[];
    currentUserId: number | null;
    isDeleting: boolean;
    isRenaming: boolean;
    onClose: () => void;
    onDelete: (mediaId: number) => Promise<void>;
    onRename: (mediaId: number, title: string) => Promise<void>;
    onDownload: (item: CalendarEntryMedia) => Promise<void>;
    onDownloadZip: (mediaIds: number[]) => Promise<void>;
    isDownloadingZip: boolean;
    isUploading: boolean;
    onUpload: (files: File[]) => Promise<void>;
    isZipUnavailable: boolean;
}

const getDisplayTitle = (item: CalendarEntryMedia): string => item.title?.trim() || item.original_filename;
const formatDateTime = (value?: string | null): string => {
    if (!value) {
        return '-';
    }
    return new Intl.DateTimeFormat('de-DE', {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(new Date(value));
};

const MediaGalleryDialog: React.FC<MediaGalleryDialogProps> = ({
    open,
    media,
    currentUserId,
    isDeleting,
    isRenaming,
    onClose,
    onDelete,
    onRename,
    onDownload,
    onDownloadZip,
    isDownloadingZip,
    isUploading,
    onUpload,
    isZipUnavailable,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const uploadInputRef = useRef<HTMLInputElement | null>(null);

    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [menuMediaId, setMenuMediaId] = useState<number | null>(null);
    const [actionMediaId, setActionMediaId] = useState<number | null>(null);
    const [renameOpen, setRenameOpen] = useState<boolean>(false);
    const [renameValue, setRenameValue] = useState<string>('');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [infoOpen, setInfoOpen] = useState<boolean>(false);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set<number>());

    const selectedMedia = useMemo(() => {
        if (selectedIndex === null) {
            return null;
        }
        return media[selectedIndex] ?? null;
    }, [media, selectedIndex]);

    const menuMedia = useMemo(() => {
        if (menuMediaId === null) {
            return null;
        }
        return media.find((item) => item.id === menuMediaId) ?? null;
    }, [media, menuMediaId]);
    const actionMedia = useMemo(() => {
        if (actionMediaId === null) {
            return null;
        }
        return media.find((item) => item.id === actionMediaId) ?? null;
    }, [actionMediaId, media]);
    const selectedCount = selectedIds.size;

    const canGoPrev = selectedIndex !== null && selectedIndex > 0;
    const canGoNext = selectedIndex !== null && selectedIndex < media.length - 1;

    const handleCloseLightbox = (): void => {
        setSelectedIndex(null);
    };

    const handleOpenLightbox = (index: number): void => {
        setSelectedIndex(index);
    };

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, mediaId: number): void => {
        setMenuAnchorEl(event.currentTarget);
        setMenuMediaId(mediaId);
    };

    const handleCloseMenu = (): void => {
        setMenuAnchorEl(null);
    };

    const handleOpenRename = (): void => {
        if (!menuMedia) {
            return;
        }
        setActionMediaId(menuMedia.id);
        setRenameValue(getDisplayTitle(menuMedia));
        setRenameOpen(true);
        handleCloseMenu();
    };

    const handleRenameSubmit = async (): Promise<void> => {
        if (!actionMedia) {
            return;
        }

        const parsed = renameMediaInputSchema.safeParse({
            mediaId: actionMedia.id,
            entryId: actionMedia.calendar_entry_id,
            title: renameValue,
        });

        if (!parsed.success) {
            return;
        }

        await onRename(actionMedia.id, parsed.data.title);
        setRenameOpen(false);
        setActionMediaId(null);
    };

    const handleOpenDeleteConfirm = (): void => {
        if (!menuMedia) {
            return;
        }
        setActionMediaId(menuMedia.id);
        setDeleteConfirmOpen(true);
        handleCloseMenu();
    };

    const handleDeleteConfirmed = async (): Promise<void> => {
        if (!actionMedia) {
            return;
        }

        const deletingId = actionMedia.id;
        await onDelete(deletingId);
        setSelectedIds((previous) => {
            const next = new Set(previous);
            next.delete(deletingId);
            return next;
        });

        if (selectedMedia?.id === deletingId) {
            setSelectedIndex(null);
        }
        setDeleteConfirmOpen(false);
        setActionMediaId(null);
    };

    const handleToggleSelected = (mediaId: number): void => {
        setSelectedIds((previous) => {
            const next = new Set(previous);
            if (next.has(mediaId)) {
                next.delete(mediaId);
            } else {
                next.add(mediaId);
            }
            return next;
        });
    };

    const handleClearSelection = (): void => {
        setSelectedIds(new Set<number>());
    };

    const handleSelectAll = (): void => {
        setSelectedIds(new Set(media.map((item) => item.id)));
    };

    const handleDownloadSelection = async (): Promise<void> => {
        if (selectedIds.size === 0) {
            return;
        }
        await onDownloadZip(Array.from(selectedIds));
    };

    const handlePickUploadFiles = (): void => {
        if (isUploading) {
            return;
        }
        uploadInputRef.current?.click();
    };

    const handleUploadChange = async ({ target }: ChangeEvent<HTMLInputElement>): Promise<void> => {
        const selectedFiles = target.files ? Array.from(target.files) : [];
        if (selectedFiles.length > 0) {
            await onUpload(selectedFiles);
        }
        target.value = '';
    };

    useEffect(() => {
        setSelectedIds((previous) => {
            const availableIds = new Set(media.map((item) => item.id));
            const next = new Set<number>();
            previous.forEach((id) => {
                if (availableIds.has(id)) {
                    next.add(id);
                }
            });
            return next;
        });
    }, [media]);

    useEffect(() => {
        if (!open) {
            setSelectedIds(new Set<number>());
            setMenuAnchorEl(null);
            setMenuMediaId(null);
            setActionMediaId(null);
            setRenameOpen(false);
            setDeleteConfirmOpen(false);
            setInfoOpen(false);
        }
    }, [open]);

    useEffect(() => {
        if (!renameOpen && !deleteConfirmOpen) {
            setActionMediaId(null);
        }
    }, [deleteConfirmOpen, renameOpen]);

    useEffect(() => {
        if (!menuAnchorEl) {
            setMenuMediaId(null);
        }
    }, [menuAnchorEl]);

    useEffect(() => {
        if (!open) {
            setSelectedIndex(null);
        }
    }, [open]);

    useEffect(() => {
        if (selectedIndex !== null && !media[selectedIndex]) {
            setSelectedIndex(null);
        }
    }, [media, selectedIndex]);

    useEffect(() => {
        if (actionMediaId !== null && !media.some((item) => item.id === actionMediaId)) {
            setActionMediaId(null);
            setRenameOpen(false);
            setDeleteConfirmOpen(false);
        }
    }, [actionMediaId, media]);

    useEffect(() => {
        if (menuMediaId !== null && !media.some((item) => item.id === menuMediaId)) {
            setMenuMediaId(null);
            setMenuAnchorEl(null);
        }
    }, [media, menuMediaId]);

    const isOwner = (item: CalendarEntryMedia): boolean => currentUserId !== null && currentUserId === item.uploader.id;

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                fullScreen={isMobile}
                maxWidth={EVENT_DIALOG_MAX_WIDTH}
                PaperProps={EVENT_DIALOG_PAPER_SX}
            >
                <DialogTitle sx={{ px: { xs: 1.25, sm: 2.5 }, py: { xs: 1, sm: 1.5 } }}>
                    {isMobile ? (
                        <Stack spacing={0.75} sx={{ width: '100%', minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                                    {`Alle Fotos (${media.length})`}
                                </Typography>
                                <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
                                    <Tooltip title="Hochladen">
                                        <span>
                                            <IconButton size="small" onClick={handlePickUploadFiles} disabled={isUploading}>
                                                <AddPhotoAlternateIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <IconButton size="small" onClick={onClose}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Stack>

                            {selectedCount > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                    {`${selectedCount} von ${media.length} ausgewählt`}
                                </Typography>
                            )}

                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={handleSelectAll}
                                    disabled={media.length === 0 || selectedCount === media.length}
                                    sx={{ minWidth: 0, px: 0.75 }}
                                >
                                    Alle
                                </Button>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={handleClearSelection}
                                    disabled={selectedCount === 0}
                                    sx={{ minWidth: 0, px: 0.75 }}
                                >
                                    Aufheben
                                </Button>
                                <Tooltip title={isZipUnavailable ? 'ZIP-Download aktuell serverseitig nicht verfügbar' : ''}>
                                    <span>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<DownloadIcon fontSize="small" />}
                                            onClick={() => void handleDownloadSelection()}
                                            disabled={selectedCount === 0 || isDownloadingZip || isZipUnavailable}
                                            sx={{ minWidth: 0, px: 0.75 }}
                                        >
                                            ZIP
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    ) : (
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: '100%' }}>
                            <Stack sx={{ minWidth: 0 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
                                    {`Alle Fotos (${media.length})`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {`${selectedCount} von ${media.length} Fotos ausgewählt`}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <Tooltip title={isZipUnavailable ? 'ZIP-Download aktuell serverseitig nicht verfügbar' : ''}>
                                    <span>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => void handleDownloadSelection()}
                                            disabled={selectedCount === 0 || isDownloadingZip || isZipUnavailable}
                                        >
                                            Auswahl herunterladen (ZIP)
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={handleSelectAll}
                                    disabled={media.length === 0 || selectedCount === media.length}
                                >
                                    Alle auswählen
                                </Button>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={handleClearSelection}
                                    disabled={selectedCount === 0}
                                >
                                    Auswahl aufheben
                                </Button>
                                <Tooltip title="Hochladen">
                                    <span>
                                        <IconButton onClick={handlePickUploadFiles} disabled={isUploading}>
                                            <AddPhotoAlternateIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <IconButton onClick={onClose}>
                                    <CloseIcon />
                                </IconButton>
                            </Stack>
                        </Stack>
                    )}
                </DialogTitle>
                <DialogContent dividers sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 }, overflowX: 'hidden' }}>
                    <input
                        ref={uploadInputRef}
                        type="file"
                        multiple
                        accept="image/*,video/*"
                        hidden
                        onChange={(event) => void handleUploadChange(event)}
                    />
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            display: 'grid',
                            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
                            gap: { xs: 0.75, sm: 1.25 },
                        }}
                    >
                        {media.map((item, index) => (
                            <Card key={item.id} variant="outlined">
                                <Box sx={{ position: 'relative' }}>
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: { xs: 4, sm: 6 },
                                            left: { xs: 4, sm: 6 },
                                            zIndex: 2,
                                            width: { xs: 22, sm: 26 },
                                            height: { xs: 22, sm: 26 },
                                            borderRadius: '50%',
                                            bgcolor: 'rgba(255,255,255,0.85)',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backdropFilter: 'blur(1px)',
                                        }}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                        }}
                                    >
                                        <Checkbox
                                            size="small"
                                            checked={selectedIds.has(item.id)}
                                            onChange={() => handleToggleSelected(item.id)}
                                            sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: { xs: 16, sm: 18 } } }}
                                        />
                                    </Box>
                                    <CardActionArea onClick={() => handleOpenLightbox(index)}>
                                        {item.type === 'image' ? (
                                            <AuthenticatedImage
                                                url={item.url}
                                                alt={getDisplayTitle(item)}
                                                sx={{ width: '100%', height: { xs: 96, sm: 140 }, objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    height: { xs: 96, sm: 140 },
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    bgcolor: 'action.hover',
                                                }}
                                            >
                                                <InsertDriveFileIcon fontSize={isMobile ? 'medium' : 'large'} color="action" />
                                            </Box>
                                        )}
                                    </CardActionArea>
                                </Box>
                                <CardContent sx={{ py: 0.5, px: 0.75, '&:last-child': { pb: 0.5 } }}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                        <Typography variant="caption" sx={{ minWidth: 0 }} noWrap>
                                            {getDisplayTitle(item)}
                                        </Typography>
                                        <Stack direction="row" spacing={0.25}>
                                            <IconButton size="small" onClick={() => void onDownload(item)}>
                                                <DownloadIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={(event) => handleOpenMenu(event, item.id)}>
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                </DialogContent>
            </Dialog>

            <Dialog
                open={selectedMedia !== null}
                onClose={handleCloseLightbox}
                fullWidth
                fullScreen={isMobile}
                maxWidth={EVENT_DIALOG_MAX_WIDTH}
                PaperProps={EVENT_DIALOG_PAPER_SX}
            >
                <DialogTitle sx={{ px: { xs: 1, sm: 2.5 }, py: { xs: 0.75, sm: 1.5 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, minWidth: 0, pr: 1 }} noWrap>
                        {selectedMedia ? getDisplayTitle(selectedMedia) : ''}
                    </Typography>
                    <Stack direction="row" spacing={0.25}>
                        {selectedMedia && (
                            <IconButton size="small" onClick={() => void onDownload(selectedMedia)}>
                                <DownloadIcon fontSize="small" />
                            </IconButton>
                        )}
                        {selectedMedia && (
                            <IconButton size="small" onClick={(event) => handleOpenMenu(event, selectedMedia.id)}>
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        )}
                        <IconButton size="small" onClick={handleCloseLightbox}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers sx={{ p: { xs: 0.75, sm: 2 }, overflowX: 'hidden' }}>
                    {selectedMedia && (
                        <Box
                            sx={{
                                position: 'relative',
                                width: '100%',
                                minHeight: { xs: 'calc(100vh - 112px)', sm: '72vh' },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'background.default',
                                borderRadius: 1.5,
                            }}
                        >
                            {!isMobile && (
                                <IconButton
                                    onClick={() => setSelectedIndex((index) => (index === null ? null : index - 1))}
                                    disabled={!canGoPrev}
                                    sx={{ position: 'absolute', left: 8, zIndex: 2 }}
                                >
                                    <NavigateBeforeIcon />
                                </IconButton>
                            )}

                            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', px: { xs: 0.5, sm: 6 } }}>
                                {selectedMedia.type === 'image' ? (
                                    <AuthenticatedImage
                                        url={selectedMedia.url}
                                        alt={getDisplayTitle(selectedMedia)}
                                        sx={{
                                            width: '100%',
                                            maxWidth: '100%',
                                            maxHeight: { xs: 'calc(100vh - 168px)', sm: '65vh' },
                                            objectFit: 'contain',
                                        }}
                                    />
                                ) : (
                                    <Stack alignItems="center" spacing={1} sx={{ py: 5 }}>
                                        <InsertDriveFileIcon fontSize="large" color="action" />
                                        <Typography variant="body2" color="text.secondary">
                                            Vorschau für diesen Dateityp nicht verfügbar.
                                        </Typography>
                                    </Stack>
                                )}
                            </Box>

                            {!isMobile && (
                                <IconButton
                                    onClick={() => setSelectedIndex((index) => (index === null ? null : index + 1))}
                                    disabled={!canGoNext}
                                    sx={{ position: 'absolute', right: 8, zIndex: 2 }}
                                >
                                    <NavigateNextIcon />
                                </IconButton>
                            )}

                            {isMobile && (
                                <>
                                    <IconButton
                                        onClick={() => setSelectedIndex((index) => (index === null ? null : index - 1))}
                                        disabled={!canGoPrev}
                                        sx={{
                                            position: 'absolute',
                                            left: 6,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            zIndex: 2,
                                            bgcolor: 'background.paper',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            width: 34,
                                            height: 34,
                                            '&:hover': { bgcolor: 'background.paper' },
                                        }}
                                    >
                                        <NavigateBeforeIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => setSelectedIndex((index) => (index === null ? null : index + 1))}
                                        disabled={!canGoNext}
                                        sx={{
                                            position: 'absolute',
                                            right: 6,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            zIndex: 2,
                                            bgcolor: 'background.paper',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            width: 34,
                                            height: 34,
                                            '&:hover': { bgcolor: 'background.paper' },
                                        }}
                                    >
                                        <NavigateNextIcon fontSize="small" />
                                    </IconButton>
                                </>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
                <MenuItem
                    onClick={() => {
                        setActionMediaId(menuMedia?.id ?? null);
                        setInfoOpen(true);
                        handleCloseMenu();
                    }}
                >
                    <InfoOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                    Informationen
                </MenuItem>
                {menuMedia && isOwner(menuMedia) && (
                    <MenuItem onClick={handleOpenRename} disabled={isRenaming}>Umbenennen</MenuItem>
                )}
                {menuMedia && isOwner(menuMedia) && (
                    <MenuItem onClick={handleOpenDeleteConfirm} disabled={isDeleting}>Löschen</MenuItem>
                )}
            </Menu>

            <Dialog
                open={infoOpen}
                onClose={() => {
                    setInfoOpen(false);
                    setActionMediaId(null);
                }}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Medieninformationen</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5} sx={{ pt: 0.5 }}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Titel</Typography>
                            <Typography variant="body2">{actionMedia ? getDisplayTitle(actionMedia) : '-'}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Datei</Typography>
                            <Typography variant="body2">{actionMedia?.original_filename ?? '-'}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Hochgeladen von</Typography>
                            <Typography variant="body2">{actionMedia?.uploader.name ?? '-'}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Hochgeladen am</Typography>
                            <Typography variant="body2">{formatDateTime(actionMedia?.created_at)}</Typography>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setInfoOpen(false);
                        setActionMediaId(null);
                    }}
                    >
                        Schließen
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={renameOpen}
                onClose={() => {
                    setRenameOpen(false);
                    setActionMediaId(null);
                }}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Medium umbenennen</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Titel"
                        value={renameValue}
                        onChange={({ target }: ChangeEvent<HTMLInputElement>) => setRenameValue(target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        sx={secondaryButtonSx}
                        onClick={() => {
                            setRenameOpen(false);
                            setActionMediaId(null);
                        }}
                    >
                        Abbrechen
                    </Button>
                    <Button onClick={() => void handleRenameSubmit()} sx={{ ...primaryButtonSx }} disabled={isRenaming || !renameValue.trim()}>
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteConfirmOpen}
                onClose={() => {
                    setDeleteConfirmOpen(false);
                    setActionMediaId(null);
                }}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Medium löschen?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">Diese Aktion kann nicht rückgängig gemacht werden.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        sx={secondaryButtonSx}
                        onClick={() => {
                            setDeleteConfirmOpen(false);
                            setActionMediaId(null);
                        }}
                    >
                        Abbrechen
                    </Button>
                    <Button onClick={() => void handleDeleteConfirmed()} sx={dangerButtonSx} disabled={isDeleting}>
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MediaGalleryDialog;
